import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { SEO } from "@/components/SEO";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft } from "lucide-react";
import { CompTicketForm } from "@/components/producer/dashboard/CompTicketForm";
import { toast } from "sonner";

interface ProducerEvent {
  id: string;
  title: string;
  date: string;
  currency: string | null;
  quantity_vip: number | null;
  quantity_standing: number | null;
  quantity_early_bird: number | null;
  ticket_title_vip: string | null;
  ticket_title_general: string | null;
  ticket_title_early_bird: string | null;
}

const CompTickets = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [events, setEvents] = useState<ProducerEvent[]>([]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate('/producers/login');
        return;
      }

      // Check producer role
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id);

      const hasProducerRole = userRoles?.some((ur: any) => ur.role === 'producer');

      if (!hasProducerRole) {
        navigate('/producers/login?error=no_access');
        return;
      }

      setUser(session.user);
      await fetchProducerEvents(session.user.id);
    } catch (error) {
      console.error('Auth check error:', error);
      navigate('/producers/login');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProducerEvents = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select(`
          id, 
          title, 
          date, 
          currency,
          quantity_vip,
          quantity_standing,
          quantity_early_bird,
          ticket_title_vip,
          ticket_title_general,
          ticket_title_early_bird
        `)
        .eq('producer_id', userId)
        .eq('status', 'published')
        .order('date', { ascending: true });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Complimentary Tickets | SLAY Producer Portal"
        description="Create complimentary tickets for your guests"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 flex flex-col">
        <Header />
        
        <main className="flex-grow container mx-auto px-4 pt-32 pb-8 md:pt-36 md:pb-12">
          <div className="max-w-xl mx-auto">
            {/* Header */}
            <div className="mb-6">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/producers/dashboard')}
                className="mb-4 -ml-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                Complimentary Tickets
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                Create free tickets for friends, family, or VIP guests
              </p>
            </div>

            {events.length === 0 ? (
              <div className="bg-muted/30 rounded-lg p-8 text-center">
                <p className="text-muted-foreground">
                  No published events found. Create an event first to issue complimentary tickets.
                </p>
                <Button 
                  onClick={() => navigate('/create-event')} 
                  className="mt-4"
                >
                  Create Event
                </Button>
              </div>
            ) : (
              <CompTicketForm 
                events={events} 
                producerId={user?.id} 
              />
            )}
          </div>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default CompTickets;
